import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { stockPortfolioService, StockTransaction } from '../../services/stockPortfolioService';
import './StockPortfolio.css';

interface TransactionDetailProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const TransactionDetail: React.FC<TransactionDetailProps> = ({ user, onClose, onToggleSidebar }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transaction, setTransaction] = useState<StockTransaction | null>(null);

  useEffect(() => {
    if (id) {
      loadTransaction();
    }
  }, [id]);

  const loadTransaction = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await stockPortfolioService.getTransactionById(id);

      if (response.success && response.transaction) {
        setTransaction(response.transaction);
      } else {
        setError('Transaction not found');
      }
    } catch (err) {
      console.error('Error loading transaction:', err);
      setError('Failed to load transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this transaction?')) return;

    try {
      const response = await stockPortfolioService.deleteTransaction(id);
      
      if (response.success) {
        navigate('/finance/stocks');
      } else {
        setError(response.message || 'Failed to delete transaction');
      }
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setError('Failed to delete transaction');
    }
  };

  if (!user) {
    return (
      <div className="transaction-detail">
        <div className="sp-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <h1>Transaction Details</h1>
        </div>
        <div className="sp-auth-required">
          <p>Please sign in to view transaction details</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="transaction-detail">
        <div className="sp-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <button className="sp-back-btn" onClick={() => navigate('/finance/stocks')}>←</button>
          <h1>Transaction Details</h1>
        </div>
        <div className="sp-loading-state">
          <div className="sp-loading-spinner"></div>
          <p>Loading transaction...</p>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="transaction-detail">
        <div className="sp-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <button className="sp-back-btn" onClick={() => navigate('/finance/stocks')}>←</button>
          <h1>Transaction Details</h1>
        </div>
        <div className="sp-empty-state">
          <p>Transaction not found</p>
          <button onClick={() => navigate('/finance/stocks')} className="sp-back-button">
            Back to Portfolio
          </button>
        </div>
      </div>
    );
  }

  const totalAmount = transaction.quantity * transaction.price_per_share;

  return (
    <div className="transaction-detail">
      <div className="sp-header">
        <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
        <button className="sp-back-btn" onClick={() => navigate('/finance/stocks')}>←</button>
        <h1>Transaction Details</h1>
        <button 
          className="sp-delete-btn"
          onClick={handleDelete}
          title="Delete transaction"
        >
          🗑️
        </button>
      </div>

      {error && (
        <div className="sp-error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="sp-main-content">
        <div className="sp-transaction-detail-card">
          <div className="sp-detail-header">
            <div className="sp-detail-code">{transaction.stock_code}</div>
            <div className={`sp-detail-type ${transaction.transaction_type}`}>
              {transaction.transaction_type.toUpperCase()}
            </div>
          </div>
          
          <div className="sp-detail-company">{transaction.company_name || 'Unknown Company'}</div>

          <div className="sp-detail-section">
            <div className="sp-detail-row">
              <span className="sp-detail-label">Quantity:</span>
              <span className="sp-detail-value">{transaction.quantity.toLocaleString()}</span>
            </div>
            <div className="sp-detail-row">
              <span className="sp-detail-label">Price per Share:</span>
              <span className="sp-detail-value">
                {stockPortfolioService.formatCurrency(transaction.price_per_share)}
              </span>
            </div>
            <div className="sp-detail-row">
              <span className="sp-detail-label">Total Amount:</span>
              <span className="sp-detail-value sp-detail-total">
                {stockPortfolioService.formatCurrency(totalAmount)}
              </span>
            </div>
          </div>

          <div className="sp-detail-section">
            <div className="sp-detail-row">
              <span className="sp-detail-label">Transaction Date:</span>
              <span className="sp-detail-value">
                {new Date(transaction.transaction_date).toLocaleDateString()}
              </span>
            </div>
            <div className="sp-detail-row">
              <span className="sp-detail-label">Created:</span>
              <span className="sp-detail-value">
                {new Date(transaction.created_at).toLocaleString()}
              </span>
            </div>
          </div>

          {transaction.notes && (
            <div className="sp-detail-section">
              <div className="sp-detail-label">Notes:</div>
              <div className="sp-detail-notes">{transaction.notes}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetail;

